import BigNumber from "bignumber.js"
import { LineSeriesOption } from "echarts"
import { MutableRefObject, useEffect, useRef, useState } from "react"
import { Button, Card, Form, Spinner } from "react-bootstrap"
import DatePicker from "react-datepicker"
import { average as findAverage } from "simple-statistics"
import { calcMarkLineOffset } from "../getSpeedChartOptionsHelpers"
import { getPortStaysData } from "../helpers/getPortStaysData"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { VesselStay } from "../hooks/useFetchVesselStays"
import { SegmentSensitivityData } from "../hooks/usePrevetGetSensitivityDataForSegment"
import {
  SelectOption,
  VesselListSingle,
  VesselSegmentInputSelection,
} from "./PrevetMain"
import { ChosenVesselForSelect, PrevetVesselSearch } from "./PrevetVesselSearch"
import { useFetchVesselSensitivityInput } from "../hooks/useFetchVesselSensitivityInput"
import { useAddVesselSensitivityInput } from "../hooks/useAddVesselSensitivityInput"
import {
  GetVesselSensitivityInputStruct,
  CreateVesselSensitivityInputObject,
} from "../hooks/usePrevetAPI"
import { RootState } from "store"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { format } from "date-fns"
import { getFuelConBySegment } from "./PrevetMain"
import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"

const hydratedKeys = new Set<string>()

interface PrevetSearchWidgetProps {
  ladenFuelCon: string
  ladenSeaDays: string
  describedLadenSpeed: string
  describedBallastSpeed: string
  ballastFuelCon: string
  ballastSeaDays: string
  setLadenFuelCon: (value: string) => void
  setLadenSeaDays: (value: string) => void
  setDescribedLadenSpeed: (value: string) => void
  setDescribedBallastSpeed: (value: string) => void
  setBallastFuelCon: (value: string) => void
  setBallastSeaDays: (value: string) => void
  segmentSensitivityData: SegmentSensitivityData | undefined
  ladenChartInstance: MutableRefObject<any>
  ballastChartInstance: MutableRefObject<any>
  speedGraphLadenAvgSpeed: number | undefined
  speedGraphBallastAvgSpeed: number | undefined
  portStaysLadenAvgSpeed: number | undefined
  portStaysBallastAvgSpeed: number | undefined
  realLadenMaxSpeed: number | undefined
  realBallastMaxSpeed: number | undefined
  setUserLadenVesselSpeed: (speed: string | undefined) => void
  setUserBallastVesselSpeed: (speed: string | undefined) => void
  vesselsData: VesselListSingle[] | undefined
  getVesselsLoading: boolean
  getVesselDetailsLoading: boolean
  chosenSegment: VesselSegmentInputSelection
  getChosenVessel: (args: ChosenVesselForSelect) => void
  setChosenSegment: (segment: VesselSegmentInputSelection) => void

  vesselSegments: VesselSegmentInputSelection[]
  clearChosenVesselData: () => void
  chosenVessel: SelectOption | undefined
  setChosenVessel: React.Dispatch<
    React.SetStateAction<SelectOption | undefined>
  >
  selectedDate: string // The selected date in yyyy-mm-dd format
  setSelectedDate: React.Dispatch<React.SetStateAction<string>> // Setter function for the date
  showTradingModal: () => void
  describedHirePrice: number
  describedFuelPrice: number
  setInitBallastSpeed: (speed: string) => void
  setInitLadenSpeed: (speed: string) => void
  isShowSeaMarginChecked: boolean
  ladenSM?: number
  ballastSM?: number
  setIsDescribedSpeedChanged: (isChanged: boolean) => void
}

function PrevetSearchWidget({
  ladenFuelCon,
  ladenSeaDays,
  describedLadenSpeed,
  describedBallastSpeed,
  ballastFuelCon,
  ballastSeaDays,
  setLadenFuelCon,
  setLadenSeaDays,
  setDescribedLadenSpeed,
  setDescribedBallastSpeed,
  setBallastFuelCon,
  setBallastSeaDays,
  ladenChartInstance,
  ballastChartInstance,
  speedGraphLadenAvgSpeed,
  speedGraphBallastAvgSpeed,
  portStaysLadenAvgSpeed,
  portStaysBallastAvgSpeed,
  realLadenMaxSpeed,
  realBallastMaxSpeed,
  segmentSensitivityData,
  setUserLadenVesselSpeed,
  setUserBallastVesselSpeed,
  vesselsData,
  getVesselsLoading,
  getVesselDetailsLoading,
  chosenSegment,
  getChosenVessel,
  clearChosenVesselData,
  chosenVessel,
  setChosenVessel,
  selectedDate,
  setSelectedDate,
  showTradingModal,
  describedFuelPrice,
  describedHirePrice,
  setInitBallastSpeed,
  setInitLadenSpeed,
  isShowSeaMarginChecked,
  ladenSM,
  ballastSM,
  setIsDescribedSpeedChanged
}: PrevetSearchWidgetProps) {
  const { setTabStateValue, getTabStateValue, getSelectedIndex, refetchTrigger, triggerRefetch } =
    usePrevetTabStateContext()
  const { createVesselSensitivityInput, isLoading } =
    useAddVesselSensitivityInput()
  const defaultHirePrice = segmentSensitivityData?.benchmark.spot_price || ""
  const defaultFuelPrice = segmentSensitivityData?.benchmark.fuel_price || ""
  const [describedBallastSpeedTouched, setDescribedBallastSpeedTouched] =
    useState(false)
  const [describedLadenSpeedTouched, setDescribedLadenSpeedTouched] =
    useState(false)
  const hirePrice = String(defaultHirePrice)
  const [fuelPrice, setFuelPrice] = useState(String(defaultFuelPrice))
  const chosenOrganisation = useSelector(
    (state: RootState) => state.user.chosenOrganization
  )
  const userInitial = useSelector((state: RootState) =>
    state.user.user?.name ? state.user.user.name : ""
  )

  const [currentSelectedIndex, setCurrentSelectedIndex] = useState(
    getSelectedIndex()
  )

  const currentKey = `${chosenOrganisation?.id || ""}-${
    chosenVessel?.value || ""
  }-${selectedDate}`

  //hide save button
  const [isSaveButtonHidden, setIsSaveButtonHidden] = useState<boolean>(true)
  const [isErrorBallastSpeed, setIsErrorBallastSpeed] = useState<boolean>(false)
  const [isErrorLadenSpeed, setIsErrorLadenSpeed] = useState<boolean>(false)

  //fetch vessel sensitivity input data
  const { vesselSensitivityInputData, fetchVesselSensitivityInput } =
    useFetchVesselSensitivityInput()
  const [currentVsi, setCurrentVsi] =
    useState<CreateVesselSensitivityInputObject>()

  const { postTrackingEvent } = useWoopraAPI()

  useEffect(() => {
    fetchVesselSensitivityInput({
      orgId: chosenOrganisation?.id || "",
      vesselImo: chosenVessel?.value.toString() || "",
      vesselDateSelected: selectedDate,
    } as GetVesselSensitivityInputStruct)
  }, [
    selectedDate,
    chosenOrganisation?.id,
    chosenVessel?.value,
    refetchTrigger,
  ])

  // Set currentVsi when vesselSensitivityInputData is available
  useEffect(() => {
    setCurrentVsi({
      orgId: chosenOrganisation?.id || "",
      vesselImo: chosenVessel?.value.toString() || "",
      vesselDateSelected: new Date(selectedDate).toISOString(),
      updatedByName: userInitial,
      data: {
        ballastKt: Number(describedBallastSpeed),
        ballastMt: Number(ballastFuelCon),
        ballastSd: Number(ballastSeaDays),
        ladenKt: Number(describedLadenSpeed),
        ladenMt: Number(ladenFuelCon),
        ladenSd: Number(ladenSeaDays),
        hirePrice: null,
        fuelPrice: null,
      },
    })
  }, [
    chosenOrganisation?.id,
    chosenVessel?.value,
    selectedDate,
    describedBallastSpeed,
    ballastFuelCon,
    ballastSeaDays,
    describedLadenSpeed,
    ladenFuelCon,
    ladenSeaDays,
    describedHirePrice,
    describedFuelPrice,
    defaultFuelPrice,
    defaultHirePrice,
  ])

  useEffect(() => {
    if (hydratedKeys.has(currentKey)) return

    if (vesselSensitivityInputData) {
      hydratedKeys.add(currentKey)
      setDescribedBallastSpeed(
        vesselSensitivityInputData.data.ballastKt?.toString() ??
          describedBallastSpeed
      )
      setInitBallastSpeed(
        vesselSensitivityInputData.data.ballastKt?.toString() ??
          describedBallastSpeed
      )
      setBallastFuelCon(
        vesselSensitivityInputData.data.ballastMt?.toString() ?? ballastFuelCon
      )
      setBallastSeaDays(
        vesselSensitivityInputData.data.ballastSd?.toString() ?? ballastSeaDays
      )
      setDescribedLadenSpeed(
        vesselSensitivityInputData.data.ladenKt?.toString() ??
          describedLadenSpeed
      )
      setInitLadenSpeed(
        vesselSensitivityInputData.data.ladenKt?.toString() ??
          describedLadenSpeed
      )
      setLadenFuelCon(
        vesselSensitivityInputData.data.ladenMt?.toString() ?? ladenFuelCon
      )
      setLadenSeaDays(
        vesselSensitivityInputData.data.ladenSd?.toString() ?? ladenSeaDays
      )
    } else {
      const hasReducerValues = !!(
        getTabStateValue("cvdLadenSpeed", undefined) ||
        getTabStateValue("cvdBallastSpeed", undefined)
      )
      if (!hasReducerValues) {
        setDescribedBallastSpeed(String(12))
        setBallastFuelCon(String(getFuelConBySegment(chosenSegment)))
        setBallastSeaDays(String(25))
        setDescribedLadenSpeed(String(12))
        setLadenFuelCon(String(getFuelConBySegment(chosenSegment)))
        setLadenSeaDays(String(25))
      }
    }
  }, [currentKey, vesselSensitivityInputData])

  // Ref to store previous values
  const previousStateValues = useRef({
    selectedIndex: currentSelectedIndex,
    chosenVessel,
    selectedDate,
    describedBallastSpeed,
    ballastFuelCon,
    ballastSeaDays,
    describedLadenSpeed,
    ladenFuelCon,
    ladenSeaDays,
  })

  const currentIndex = getSelectedIndex()
  useEffect(() => {
    setCurrentSelectedIndex(currentIndex)
    previousStateValues.current = {
      ...previousStateValues.current,
      selectedIndex: currentIndex,
    }
  }, [currentIndex])

  useEffect(() => {
    setFuelPrice(String(segmentSensitivityData?.benchmark.fuel_price))
  }, [segmentSensitivityData?.benchmark.fuel_price])

  useEffect(() => {
    const ladenAvgSpeedForCalculation = speedGraphLadenAvgSpeed
      ? speedGraphLadenAvgSpeed
      : portStaysLadenAvgSpeed
    const ballastAvgSpeedForCalculation = speedGraphBallastAvgSpeed
      ? speedGraphBallastAvgSpeed
      : portStaysBallastAvgSpeed

    if (!ladenAvgSpeedForCalculation && !ballastAvgSpeedForCalculation) {
      return
    }
  }, [
    describedLadenSpeed,
    describedBallastSpeed,
    portStaysLadenAvgSpeed,
    portStaysBallastAvgSpeed,
    speedGraphLadenAvgSpeed,
    speedGraphBallastAvgSpeed,
    ladenFuelCon,
    ballastFuelCon,
    hirePrice,
    fuelPrice,
    ladenSeaDays,
    ballastSeaDays,
    isShowSeaMarginChecked,
  ])

  function updateChart({
    isLaden,
    speed,
    isReset,
  }: {
    isLaden: boolean
    speed: string
    isReset: boolean
  }) {
    if (
      (isLaden && !speedGraphLadenAvgSpeed) ||
      (!isLaden && !speedGraphBallastAvgSpeed)
    ) {
      return
    }
    const avgSpeed = isLaden
      ? speedGraphLadenAvgSpeed
      : speedGraphBallastAvgSpeed

    const chart = isLaden ? ladenChartInstance : ballastChartInstance
    const opts = chart.current.getOption()

    const maxYAxis = isLaden ? realLadenMaxSpeed : realBallastMaxSpeed

    const yAxis = !maxYAxis // Grow or shrink yAxis depending on user input
      ? [{ ...opts.yAxis[0] }]
      : [
          {
            ...opts.yAxis[0],
            max: [
              Number(speed) > maxYAxis // If user input TC desc > speed graph max
                ? Number(speed) + 1
                : BigNumber(maxYAxis + 1)
                    .decimalPlaces(0, BigNumber.ROUND_HALF_CEIL)
                    .toString(),
            ],
          },
        ]

    // When focusing any input, draw the usr vessel line on speed graph
    const existingSeries = opts.series as LineSeriesOption[]
    const updatedSeries = [...existingSeries]
    updatedSeries[3] = !isReset
      ? {
          name: `User Vsl`,
          markLine: {
            symbolSize: 7,
            lineStyle: {
              width: 1,
              opacity: 0.75,
            },
            data: [
              {
                name: "user vessel",
                yAxis: Number(speed),
                label: {
                  offset: calcMarkLineOffset({
                    label: Number(speed),
                    compared: avgSpeed as number,
                  }),
                  fontSize: 10,
                  formatter: (params: any) => {
                    return `${BigNumber(params.data.yAxis).toFormat(
                      1
                    )} User Vsl`
                  },
                },
              },
            ],
          },
        }
      : {
          name: "User Vsl",
          type: "line",
          markLine: {
            lineStyle: {
              width: 0,
              opacity: 0,
            },
          },
        }
    chart.current.setOption({
      ...opts,
      series: updatedSeries,
      yAxis,
    })
  }

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = date.toISOString().split("T")[0] // Format: yyyy-mm-dd
      setSelectedDate(formattedDate) // Update the state in the parent
      // setTabStateValue("date", formattedDate, undefined)
    }
  }

  const handleTodayButton = () => {
    setSelectedDate(new Date().toISOString().split("T")[0])
  }

  //handle submit for vessel sensitivity input
  const handleAddVesselSensitivityInput = async (
    vsi: CreateVesselSensitivityInputObject
  ) => {
    try {
      if (
        vesselSensitivityInputData?.data.fuelPrice &&
        vesselSensitivityInputData?.data.hirePrice
      ) {
        vsi.data.fuelPrice = vesselSensitivityInputData.data.fuelPrice
        vsi.data.hirePrice = vesselSensitivityInputData.data.hirePrice
      }
      await createVesselSensitivityInput(vsi)
      const message = "Saved vessel description and sea days duration"
      toast.success(message, {
        position: "bottom-right",
        autoClose: 8000,
      })
      triggerRefetch()
      hydratedKeys.delete(currentKey)

      // Fetch the updated vessel sensitivity input data
      await fetchVesselSensitivityInput({
        orgId: vsi.orgId,
        vesselDateSelected: selectedDate,
        vesselImo: vsi.vesselImo,
      })
      setIsDescribedSpeedChanged(false)
    } catch (error) {
      toast.error("Failed to save vessel sensitivity input", {
        position: "bottom-right",
        autoClose: 8000,
      })
    }
  }

  useEffect(() => {
    try {
      if (parseFloat(describedBallastSpeed) > 20) {
        setIsErrorBallastSpeed(true)
        return
      }
      if (parseFloat(describedLadenSpeed) > 20) {
        setIsErrorLadenSpeed(true)
        return
      }
      setIsErrorBallastSpeed(false)
      setIsErrorLadenSpeed(false)
    } catch {
      return
    }
  }, [describedBallastSpeed, describedLadenSpeed])

  return (
    <Card className="PrevetContainer_SearchWidget">
      <Card.Body>
        <PrevetVesselSearch
          vesselsData={vesselsData}
          getVesselsLoading={getVesselsLoading}
          getVesselDetailsLoading={getVesselDetailsLoading}
          getChosenVessel={getChosenVessel}
          clearChosenVesselData={clearChosenVesselData}
          chosenVessel={chosenVessel}
          setChosenVessel={setChosenVessel}
          showRecentSearch={false}
        />
        {/* Ballast and laden speed */}
        <div className="text-muted mt-2" style={{ fontSize: "0.75rem" }}>
          {`Circulated Vessel Description (${
            vesselSensitivityInputData
              ? "last inserted by: " +
                vesselSensitivityInputData.updatedByName +
                " " +
                format(
                  new Date(vesselSensitivityInputData.updatedAt),
                  "yyyy-MM-dd"
                )
              : "insert"
          })`}
        </div>
        <div
          className={`PrevetSearchCVD${
            !isSaveButtonHidden ? " PrevetSearchCVD_Open" : ""
          } border border-gray rounded px-2 pt-3 d-flex flex-column gap-0`}
        >
          {/* Vessel Sensitivity Input Form */}
          <div className="d-flex flex-nowrap align-items-center justify-content-between">
            <div
              className="PrevetCards_CircVesselText text-center"
              style={{ color: "#ff5b29" }}
            >
              Ballast
            </div>
            {/* kts - Ballast */}
            <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
              <Form.Control
                className={`PrevetCards_CircVesselForm my-auto ${
                  isErrorBallastSpeed ? "border-danger" : ""
                }`}
                placeholder="Ballast Speed"
                aria-label="Described speed in Knots"
                disabled={
                  Boolean(speedGraphBallastAvgSpeed) === false &&
                  Boolean(portStaysBallastAvgSpeed) === false
                }
                value={
                  describedBallastSpeedTouched
                    ? describedBallastSpeed
                    : BigNumber(describedBallastSpeed).toFormat(1)
                }
                onFocus={(e) => {
                  updateChart({
                    isLaden: false,
                    speed: e.target.value,
                    isReset: false,
                  })
                  updateChart({
                    isLaden: true,
                    speed: describedLadenSpeed,
                    isReset: false,
                  })
                  setUserBallastVesselSpeed(e.target.value)
                  setUserLadenVesselSpeed(describedLadenSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                  setIsSaveButtonHidden(false)
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "change_vessel_desc",
                    properties: {
                      field: "kt",
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                }}
                onChange={(e) => {
                  setDescribedBallastSpeedTouched(true)
                  setIsDescribedSpeedChanged(true)
                  setDescribedBallastSpeed(e.target.value)
                  setUserBallastVesselSpeed(e.target.value)
                  updateChart({
                    isLaden: false,
                    speed: e.target.value,
                    isReset: false,
                  })
                }}
              />
              <div className="PrevetCards_CircVesselText_SubText">kt</div>
              <div
                className={`PrevetCards_CircVesselText_SubText_Error ${
                  isErrorBallastSpeed ? "" : "d-none"
                }`}
              >
                {"Value should be < 20 kt"}
              </div>
              <div className="fs-4 px-1">/</div>
            </div>

            {/* MT - Ballast */}
            <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
              <Form.Control
                className="PrevetCards_CircVesselForm my-auto"
                aria-label="Metric Tons"
                value={ballastFuelCon}
                disabled={
                  Boolean(speedGraphBallastAvgSpeed) === false &&
                  Boolean(portStaysBallastAvgSpeed) === false
                }
                onFocus={(e) => {
                  updateChart({
                    isLaden: true,
                    speed: describedLadenSpeed,
                    isReset: false,
                  })
                  updateChart({
                    isLaden: false,
                    speed: describedBallastSpeed,
                    isReset: false,
                  })
                  setUserLadenVesselSpeed(describedLadenSpeed)
                  setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                  setIsSaveButtonHidden(false)
                }}
                onChange={(e) => {
                  setBallastFuelCon(e.target.value)
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "change_vessel_desc",
                    properties: {
                      field: "mt",
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                  // setTabStateValue("cvdBallastFuelCon", e.target.value, undefined)
                }}
              />
              <div className="PrevetCards_CircVesselText_SubText">mt</div>
              <div className="fs-4 px-1">/</div>
            </div>
            {/* Sea days - ballast */}
            <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
              <Form.Control
                className="PrevetCards_CircVesselForm my-auto"
                aria-label="Days"
                value={ballastSeaDays}
                disabled={
                  Boolean(speedGraphBallastAvgSpeed) === false &&
                  Boolean(portStaysBallastAvgSpeed) === false
                }
                onFocus={(e) => {
                  updateChart({
                    isLaden: true,
                    speed: describedLadenSpeed,
                    isReset: false,
                  })
                  updateChart({
                    isLaden: false,
                    speed: describedBallastSpeed,
                    isReset: false,
                  })
                  setUserLadenVesselSpeed(describedLadenSpeed)
                  setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                  setIsSaveButtonHidden(false)
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "change_vessel_desc",
                    properties: {
                      field: "days",
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                }}
                onChange={(e) => {
                  setBallastSeaDays(e.target.value)
                  // setTabStateValue("cvdBallastSeaDays", e.target.value, undefined)
                }}
              />
              <div className="PrevetCards_CircVesselText_SubText_SeaDays">
                sea days
              </div>
            </div>
            {/* divider  */}
            <div className="text-muted fs-4 mx-2">|</div>
            {/* Laden */}
            <div
              className="PrevetCards_CircVesselText"
              style={{ color: "#001546" }}
            >
              Laden
            </div>
            <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
              {/* kts - Laden */}
              <Form.Control
                className={`PrevetCards_CircVesselForm my-auto ${
                  isErrorLadenSpeed ? "border-danger" : ""
                }`}
                aria-label="Knots"
                disabled={
                  Boolean(speedGraphLadenAvgSpeed) === false &&
                  Boolean(portStaysLadenAvgSpeed) === false
                }
                placeholder="Laden Speed"
                value={
                  describedLadenSpeedTouched
                    ? describedLadenSpeed
                    : BigNumber(describedLadenSpeed).toFormat(1)
                }
                onFocus={(e) => {
                  updateChart({
                    isLaden: true,
                    speed: e.target.value,
                    isReset: false,
                  })
                  updateChart({
                    isLaden: false,
                    speed: describedBallastSpeed,
                    isReset: false,
                  })
                  setUserLadenVesselSpeed(e.target.value)
                  setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                  setIsSaveButtonHidden(false)
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "change_vessel_desc",
                    properties: {
                      field: "kt",
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                }}
                onChange={(e) => {
                  setDescribedLadenSpeedTouched(true)
                  setIsDescribedSpeedChanged(true)
                  setDescribedLadenSpeed(e.target.value)
                  setUserLadenVesselSpeed(e.target.value)
                  updateChart({
                    isLaden: true,
                    speed: e.target.value,
                    isReset: false,
                  })
                }}
              />
              <div className="PrevetCards_CircVesselText_SubText">kt</div>
              <div
                className={`PrevetCards_CircVesselText_SubText_Error ${
                  isErrorLadenSpeed ? "" : "d-none"
                }`}
              >
                {"Value should be < 20 kt"}
              </div>
              <div className="fs-4 px-1">/</div>
            </div>
            <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
              {/* MT - laden */}
              <Form.Control
                className="PrevetCards_CircVesselForm my-auto"
                aria-label="Metric Tons"
                value={ladenFuelCon}
                disabled={
                  Boolean(speedGraphLadenAvgSpeed) === false &&
                  Boolean(portStaysLadenAvgSpeed) === false
                }
                onFocus={(e) => {
                  updateChart({
                    isLaden: true,
                    speed: describedLadenSpeed,
                    isReset: false,
                  })
                  updateChart({
                    isLaden: false,
                    speed: describedBallastSpeed,
                    isReset: false,
                  })
                  setUserLadenVesselSpeed(describedLadenSpeed)
                  setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                  setIsSaveButtonHidden(false)
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "change_vessel_desc",
                    properties: {
                      field: "mt",
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                }}
                onChange={(e) => {
                  setLadenFuelCon(e.target.value)
                  // setTabStateValue("cvdLadenFuelCon", e.target.value, undefined)
                }}
              />
              <div className="PrevetCards_CircVesselText_SubText">mt</div>
              <div className="fs-4 px-1">/</div>
            </div>
            <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
              {/* Sea days - Laden */}
              <Form.Control
                className="PrevetCards_CircVesselForm my-auto"
                aria-label="Days"
                disabled={
                  Boolean(speedGraphLadenAvgSpeed) === false &&
                  Boolean(portStaysLadenAvgSpeed) === false
                }
                value={ladenSeaDays}
                onFocus={(e) => {
                  updateChart({
                    isLaden: true,
                    speed: describedLadenSpeed,
                    isReset: false,
                  })
                  updateChart({
                    isLaden: false,
                    speed: describedBallastSpeed,
                    isReset: false,
                  })
                  setUserLadenVesselSpeed(describedLadenSpeed)
                  setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                  setIsSaveButtonHidden(false)
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "change_vessel_desc",
                    properties: {
                      field: "days",
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                }}
                onChange={(e) => {
                  setLadenSeaDays(e.target.value)
                  // setTabStateValue("cvdLadenSeaDays", e.target.value, undefined)
                }}
              />
              <div className="PrevetCards_CircVesselText_SubText_SeaDays">
                sea days
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-end mt-0">
            {isSaveButtonHidden ? null : (
              <Button
                variant="primary"
                className="PrevetCards_SaveSensitivityButton d-flex align-items-center justify-content-center"
                onClick={() => {
                  handleAddVesselSensitivityInput(
                    currentVsi as CreateVesselSensitivityInputObject
                  )
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "click_save_vessel_description",
                    properties: {
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                }}
                disabled={isErrorLadenSpeed || isErrorBallastSpeed || isLoading}
              >
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    variant="light"
                  />
                ) : (
                  <>
                    <span className="ti-save" />
                    <span className="ms-1">Save</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
        {/* Date section */}
        <div
          className="d-flex flex-nowrap justify-content-between align-items-end mt-2"
          style={{ width: "calc(100% - 10px)" }}
        >
          {/* Select Date Section */}
          <div>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              Select Date
            </div>
            <div className="d-flex position-relative">
              <button
                className={`btn ${
                  selectedDate === new Date().toISOString().split("T")[0]
                    ? "btn-muted border border-gray"
                    : "btn-primary"
                } position-absolute h-100`}
                onClick={handleTodayButton}
                style={{
                  zIndex: "100",
                  width: "50px",
                  fontSize: "0.7rem",
                  padding: "0",
                }}
                disabled={
                  selectedDate === new Date().toISOString().split("T")[0]
                    ? true
                    : false
                }
              >
                Today
              </button>
              <DatePicker
                selected={new Date(selectedDate)}
                onChange={(e) => {
                  handleDateChange(e)
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "change_date",
                    properties: {
                      vesselSegment: chosenSegment.value,
                      component: "vessel_search",
                    },
                  })
                }}
                dateFormat="yyyy-MM-dd"
                maxDate={new Date()}
                minDate={new Date("2021-01-01")}
                className="form-control ps-6 w-100"
                wrapperClassName="w-100"
                showIcon={true}
              />
            </div>
          </div>
          {/* Trade Map Button */}
          <Button
            variant="primary"
            className="PrevetTrio_SpeedGraph_OceanZoneButton"
            onClick={() => {
              showTradingModal()
              postTrackingEvent({
                page: "/prevetting",
                event: "click_trade_map",
                properties: {
                  button: "trade_map",
                  vesselSegment: chosenSegment.value,
                  component: "vessel_search",
                },
              })
            }}
          >
            Trade Map
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

export function getAvgSpeedFromPortStays({
  vesselStaysData,
  isLaden,
}: {
  vesselStaysData: VesselStay[] | undefined
  isLaden: boolean
}) {
  if (!vesselStaysData || vesselStaysData.length < 1) {
    return undefined
  }
  const portStaysData = getPortStaysData(vesselStaysData)
  const stays = isLaden ? portStaysData.laden : portStaysData.ballast

  if (!stays || stays.length < 1) {
    return undefined
  }

  return findAverage(stays.map((stay) => stay.avg_speed_mov))
}

export default PrevetSearchWidget
